import { ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useMediaPredicate } from 'react-media-hook';
import { useTranslation } from 'react-i18next';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet';
import { Button, buttonVariants } from 'components/ui/button';
import Icon from 'components/icon/Icon';
import { PATH_ROUTES } from 'routes/path';
import useWalkthrough from 'hooks/useWalkthrough';
import { WALKTHROUGH_CHECK } from 'lib/constants';
import useTourStore from 'store/app-tour';

interface SettingsSheetProps {
  open: boolean;
  onClose: () => void;
  onSidebarClose?: () => void;
}

export default function SettingsSheet({
  open,
  onClose,
  onSidebarClose,
}: SettingsSheetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMediaMobile = useMediaPredicate('(max-width: 640px)');

  const { deleteStep } = useWalkthrough();
  const { setStartSideMenu } = useTourStore();

  const onAppWalkthrough = () => {
    deleteStep(WALKTHROUGH_CHECK.SIDEMENU);
    onClose();
    onSidebarClose?.();
    navigate(PATH_ROUTES.DASHBOARD);
    setStartSideMenu(true);
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side='left'
        onOpenAutoFocus={(e) => e.preventDefault()}
        className='!w-full'
        hideClose={!isMediaMobile}
      >
        <SheetHeader>
          <SheetTitle className='text-xl font-semibold text-center'>
            {t('settings')}
          </SheetTitle>
        </SheetHeader>

        <div className='mt-6 space-y-4'>
          <Button
            variant='ghost'
            className='justify-between w-full'
            onClick={onAppWalkthrough}
          >
            <div className='flex items-center gap-x-4'>
              <Icon icon='information' size={20} />
              <p>{t('app_walkthrough')}</p>
            </div>

            <ChevronRight className='size-6 text-primary' />
          </Button>

          <Link
            to={PATH_ROUTES.SEND_FEEDBACK}
            onClick={() => {
              onClose();
              onSidebarClose?.();
            }}
            className={buttonVariants({
              variant: 'ghost',
              className: 'cursor-pointer justify-between w-full !px-4',
            })}
          >
            <div className='flex items-center gap-x-4'>
              <Icon icon='report' size={20} />
              <p>{t('send_feedback')}</p>
            </div>

            <ChevronRight className='size-6 text-primary' />
          </Link>

          <a
            href={process.env.REACT_APP_MEDSI_PRIVACY}
            target='_blank'
            rel='noopener noreferrer'
            className={buttonVariants({
              variant: 'ghost',
              className: 'cursor-pointer justify-between w-full !px-4',
            })}
          >
            <div className='flex items-center gap-x-4'>
              <Icon icon='report' size={20} />
              <p>{t('pp')}</p>
            </div>

            <ChevronRight className='size-6 text-primary' />
          </a>

          <Link
            to={PATH_ROUTES.COMMUNICATION_PREFERENCE}
            onClick={() => {
              onClose();
              onSidebarClose?.();
            }}
            className={buttonVariants({
              variant: 'ghost',
              className: 'cursor-pointer justify-between w-full !px-4',
            })}
          >
            <div className='flex items-center gap-x-4'>
              <Icon icon='settings' size={20} />
              <p>{t('communication_preference')}</p>
            </div>

            <ChevronRight className='size-6 text-primary' />
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}
